<template>
  <div class="d-flex flex-column-fluid analytics-dashboard">
    <div class="container">

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner-border" role="status"></div>
        <div class="loading-text">Loading analytics data...</div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <div class="alert alert-danger">
          <i class="material-icons">error</i>
          {{ error }}
        </div>
      </div>

      <!-- Main Content -->
      <div v-else>
        <!-- Page Header -->
        <div class="page-header mb-6">
          <div class="header-content">
            <div class="header-title">
              <h1 class="title">Analytics Dashboard</h1>
              <p class="subtitle">Monitor financing performance and business insights</p>
            </div>
            <div class="header-actions">
              <div class="business-filter">
                <label class="form-label font-weight-bold mb-2">Filter by Business</label>
                                  <div class="business-selector">
                    <div class="search-wrapper">
                      <input
                        ref="businessSearchInput"
                        type="text"
                        class="form-control search-input"
                        v-model="businessSearchTerm"
                        @input="searchBusinesses"
                        @focus="onSearchFocus"
                        @blur="onSearchBlur"
                        :placeholder="selectedBusinesses.length > 0 ? `${selectedBusinesses.length} business(es) selected` : 'Type to search businesses...'"
                        :disabled="loadingBusinesses"
                      />
                      <i v-if="!loadingBusinesses" class="las la-search search-icon"></i>
                      <i v-else class="las la-spinner la-spin search-icon"></i>
                    </div>
                  <!-- Debug button to test dropdown -->

                  
                  <div 
                    v-if="showBusinessDropdown" 
                    class="business-dropdown"
                    ref="businessDropdown"
                    @mouseenter="onDropdownMouseEnter"
                    @mouseleave="onDropdownMouseLeave"
                  >
                    
                    <div v-if="loadingBusinesses && filteredBusinesses.length === 0" class="dropdown-loading">
                      <div class="spinner-border spinner-border-sm mr-2"></div>
                      Loading businesses from companies API...
                    </div>
                    
                    <div v-else-if="filteredBusinesses.length === 0 && !loadingBusinesses" class="dropdown-empty">
                      No businesses found
                    </div>
                    
                    <div v-else class="business-list" @scroll="onDropdownScroll" ref="businessList">
                      <div 
                        v-for="business in filteredBusinesses" 
                        :key="business.id"
                        class="business-item"
                        :class="{ 'selected': selectedBusinesses.some(b => b.id === business.id) }"
                        @click.stop="toggleBusinessSelection(business)"
                      >
                        <span class="business-name">{{ business.name }}</span>
                        <span class="business-action">
                          <i v-if="selectedBusinesses.some(b => b.id === business.id)" class="las la-check text-success" title="Selected"></i>
                          <i v-else class="las la-plus text-primary" title="Select"></i>
                        </span>
                      </div>
                      
                      <!-- Loading more indicator -->
                      <div v-if="isLoadingMore" class="dropdown-loading-more">
                        <div class="spinner-border spinner-border-sm mr-2"></div>
                        Loading more businesses...
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Selected Business Display -->
                <div v-if="selectedBusinesses.length > 0" class="selected-business-display mt-2">
                  <div class="selected-business-chips">
                    <span v-for="business in selectedBusinesses" :key="business.id" class="business-chip">
                      <span class="business-name">{{ business.name }}</span>
                      <button 
                        type="button" 
                        class="remove-btn"
                        @click="removeBusinessSelection(business)"
                        :disabled="loading"
                        title="Remove selection"
                      >
                        <i class="las la-times"></i>
                      </button>
                    </span>
                    <button 
                      type="button" 
                      class="clear-all-btn"
                      @click="clearAllBusinessSelections"
                      :disabled="loading"
                      title="Clear all selections"
                    >
                      <i class="las la-times-circle"></i>
                      Clear All
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Key Metrics Cards -->
        <div class="row metric-cards mb-8">
          <div class="col-xl-3">
            <div class="card card-custom card-stretch gutter-b metric-card">
              <div class="card-body">
                <span class="ml-n1 metric-icon">
                </span>
                <div class="font-weight-bold text-muted font-size-sm metric-label">
                  Total Receivable Amount
                </div>
                <div class="text-dark-75 font-weight-bolder font-size-h2 mt-3 metric-value">
                  {{ formatCurrencyCompact(aggregateData.total_receivable_amount, aggregateData.currency) }}
                </div>
              </div>
            </div>
          </div>

          <div class="col-xl-3">
            <div class="card card-custom card-stretch gutter-b metric-card">
              <div class="card-body">
                <span class="ml-n1 metric-icon">
                </span>
                <div class="font-weight-bold text-muted font-size-sm metric-label">
                  Total Disbursed Amount
                </div>
                <div class="text-dark-75 font-weight-bolder font-size-h2 mt-3 metric-value">
                  {{ formatCurrencyCompact(aggregateData.total_disbursed_amount, aggregateData.currency) }}
                </div>
              </div>
            </div>
          </div>

          <div class="col-xl-3">
            <div class="card card-custom card-stretch gutter-b metric-card">
              <div class="card-body">
                <span class="ml-n1 metric-icon">
                </span>
                <div class="font-weight-bold text-muted font-size-sm metric-label">
                  Total Fees
                </div>
                <div class="text-dark-75 font-weight-bolder font-size-h2 mt-3 metric-value">
                  {{ formatCurrencyCompact(aggregateData.total_fees, aggregateData.currency) }}
                </div>
              </div>
            </div>
          </div>

          <div class="col-xl-3">
            <div class="card card-custom card-stretch gutter-b metric-card">
              <div class="card-body">
                <span class="ml-n1 metric-icon">
                </span>
                <div class="font-weight-bold text-muted font-size-sm metric-label">
                  Total Requests
                </div>
                <div class="text-dark-75 font-weight-bolder font-size-h2 mt-3 metric-value">
                  {{ aggregateData.requests || 0 }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Recent Requests and Charts Row -->
        <div class="row mb-8">
          <!-- Recent Requests Preview (50% width) -->
          <div class="col-lg-6">
            <div class="card card-custom requests-preview h-100">
              <div class="card-header">
                <div class="card-title">
                  <h3 class="card-label">
                    Recent Financing Requests
                    <span class="text-muted pt-2 font-size-sm d-block">
                      Latest financing requests (showing {{ previewRequests.length }} of {{ totalRequests }})
                    </span>
                  </h3>
                </div>
                <div class="card-toolbar header-actions">
                  <router-link 
                    :to="{ name: 'financing-requests' }" 
                    class="btn btn-dark btn-view-all"
                  >
                    View All ({{ totalRequests }})
                  </router-link>
                </div>
              </div>
              <div class="card-body">
                <div class="table-responsive requests-table">
                  <table class="table table-head-custom table-vertical-center">
                    <thead>
                      <tr class="text-left">
                        <th>Invoice Number</th>
                        <th>Borrower</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="request in previewRequests" :key="request.id">
                        <td>
                          <span class="invoice-reference">{{ request.invoice_reference }}</span>
                        </td>
                        <td>
                          <span class="borrower-name">{{ request.borrower_name }}</span>
                        </td>
                        <td>
                          <span class="amount-value">
                            {{ formatCurrency(request.total_receivable_amount, aggregateData.currency) }}
                          </span>
                        </td>
                        <td>
                          <span :class="['status-badge', getStatusClass(request.status)]">
                            {{ formatStatus(request.status) }}
                          </span>
                        </td>
                        <td>
                          <span class="date-value">{{ formatDate(request.created_at) }}</span>
                        </td>
                      </tr>
                      <tr v-if="!previewRequests.length && !loadingRequests">
                        <td colspan="4" class="text-center">
                          <div class="empty-state">
                            <div class="empty-icon">📋</div>
                            <div class="empty-title">No Requests</div>
                            <div class="empty-text">No financing requests found</div>
                          </div>
                        </td>
                      </tr>
                      <tr v-if="loadingRequests">
                        <td colspan="4" class="text-center">
                          <div class="loading-state">
                            <div class="spinner-border"></div>
                            <div class="loading-text">Loading requests...</div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Outstanding Repayments & Fees Section (50% width) -->
          <div class="col-lg-6">
            <div class="card card-custom h-100">
              <div class="card-header">
                <div class="card-title">
                  <h3 class="card-label">
                    Outstanding Repayments & Fees
                    <span class="text-muted pt-2 font-size-sm d-block">
                      All outstanding repayment & fees schedules ({{ totalRepayments }} total)
                    </span>
                  </h3>
                </div>
                <div class="card-toolbar header-actions">
                  <router-link 
                    :to="{ name: 'upcoming-repayments' }" 
                    class="btn btn-dark btn-view-all"
                  >
                    View All ({{ totalRepayments }})
                  </router-link>
                </div>
              </div>
              <div class="card-body">
                <div class="table-responsive repayments-table">
                  <table class="table table-head-custom table-vertical-center">
                    <thead>
                      <tr class="text-left">
                        <th>Invoice Number</th>
                        <th>Borrower</th>
                        <th>Amount Due</th>
                        <th>Due Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="repayment in repayments" :key="repayment.id">
                        <td>
                          <span class="invoice-reference">{{ repayment.invoice_reference }}</span>
                        </td>
                        <td>
                          <span class="borrower-name">{{ repayment.borrower_name }}</span>
                        </td>
                        <td>
                          <span class="amount-value">
                            {{ formatCurrency(repayment.amount_due, repayment.currency) }}
                          </span>
                        </td>
                        <td>
                          <span class="date-value" :class="{ 'overdue': isOverdue(repayment.due_date) }">
                            {{ formatDate(repayment.due_date) }}
                          </span>
                        </td>
                        <td>
                          <span :class="['status-badge', getRepaymentStatusClass(repayment.due_status)]">
                            {{ formatRepaymentStatus(repayment.due_status) }}
                          </span>
                        </td>
                      </tr>
                      <tr v-if="!repayments.length && !loadingRepayments">
                        <td colspan="5" class="text-center">
                          <div class="empty-state">
                            <div class="empty-icon">📋</div>
                            <div class="empty-title">No Repayments</div>
                            <div class="empty-text">No outstanding repayment & fees schedules found</div>
                          </div>
                        </td>
                      </tr>
                      <tr v-if="loadingRepayments">
                        <td colspan="5" class="text-center">
                          <div class="loading-state">
                            <div class="spinner-border"></div>
                            <div class="loading-text">Loading repayments...</div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Business Selection and Monthly Analytics -->
        <div class="row mb-8">
          <div class="col-12">
            <div class="card card-custom monthly-analytics-section">
              <div class="card-header">
                <div class="card-title">
                  <h3 class="card-label">
                    Monthly Analytics
                    <span class="text-muted pt-2 font-size-sm d-block">
                      Monthly breakdown of financing activity
                      <span v-if="selectedBusinesses.length > 0" class="text-primary">
                        (Filtered by: {{ selectedBusinesses.map(b => b.name).join(', ') }})
                      </span>
                    </span>
                  </h3>
                </div>
              </div>
              <div class="card-body">
                <div class="table-responsive monthly-analytics-table">
                  <table class="table table-head-custom table-vertical-center">
                    <colgroup>
                      <col style="width: 10%">
                      <col style="width: 24%">
                      <col style="width: 20%">
                      <col style="width: 24%">
                      <col style="width: 20%">
                    </colgroup>
                    <thead>
                      <tr class="text-left">
                        <th>Month</th>
                        <th class="text-right">Total Purchased Receivables Amount</th>
                        <th class="text-right">Total Fees Amount</th>
                        <th class="text-right">Total Disbursed Amount</th>
                        <th class="text-center">Unique Business Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in monthlyAnalytics" :key="item.month">
                        <td>
                          <span class="month-cell">{{ formatMonth(item.month) }}</span>
                        </td>
                        <td class="text-right">
                          <span class="amount-cell receivable" v-if="item.monthly_receivable_amount">
                            {{ formatCurrencyCompact(item.monthly_receivable_amount, aggregateData.currency) }}
                          </span>
                          <span class="no-data" v-else>-</span>
                        </td>
                        <td class="text-right">
                          <span class="amount-cell fees" v-if="item.monthly_receivable_amount">
                            {{ formatCurrencyCompact(item.monthly_total_fees_amount, aggregateData.currency) }}
                          </span>
                          <span class="no-data" v-else>-</span>
                        </td>
                        <td class="text-right">
                          <span class="amount-cell disbursed" v-if="item.monthly_disbursed_amount">
                            {{ formatCurrencyCompact(item.monthly_disbursed_amount, aggregateData.currency) }}
                          </span>
                          <span class="no-data" v-else>-</span>
                        </td>
                        <td class="text-center">
                          <span class="business-count">{{ item.unique_business_count || 0 }}</span>
                        </td>
                      </tr>
                      <tr v-if="!monthlyAnalytics.length">
                        <td colspan="4" class="text-center">
                          <div class="empty-state">
                            <div class="empty-icon">📊</div>
                            <div class="empty-title">No Monthly Data</div>
                            <div class="empty-text">No monthly analytics data available</div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FinancingService from "@/services/financing.service";
import CompaniesService from "@/services/companies.service";
import { formatCurrencyCompact } from "@/utils/number-formatter";
import { formatErrorMessage } from "@/utils/random_utils";

export default {
  name: "FinancingAnalyticsDashboard",
  data() {
    return {
      loading: true,
      error: null,
      
      // Aggregate data
      aggregateData: {
        total_receivable_amount: 0,
        total_disbursed_amount: 0,
        total_fees: 0,
        requests: 0,
        currency: 'AED',
        monthly_analytics: []
      },
      
      // Monthly analytics
      monthlyAnalytics: [],
      
      // Requests preview
      previewRequests: [],
      loadingRequests: false,
      totalRequests: 0,
      
      // Repayments data
      repayments: [],
      loadingRepayments: false,
      totalRepayments: 0,
      
      // Business selection
      selectedBusinesses: [], // Changed from selectedBusiness to selectedBusinesses array
      availableBusinesses: [],
      businessSearchTerm: "",
      showBusinessDropdown: false,
      loadingBusinesses: false,
      businessSearchDebounce: null,
      scrollDebounce: null,
      businessPage: 1,
      hasMoreBusinesses: false,
      totalBusinesses: 0,
      isInteractingWithDropdown: false,
      isLoadingMore: false
    };
  },
  
  async   mounted() {
    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener('keydown', this.handleKeydown);
    await this.loadDashboardData();
    await this.loadInitialBusinesses();
  },
  
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleKeydown);
    clearTimeout(this.businessSearchDebounce);
    clearTimeout(this.scrollDebounce);
  },
  
  methods: {
    async loadDashboardData() {
      try {
        this.loading = true;
        this.error = null;
        
        // Prepare query parameters
        const params = {};
        if (this.selectedBusinesses.length > 0) {
          params.borrower_ids = this.selectedBusinesses.map(b => b.id).join(',');
        }
        
        // Load aggregate data
        const aggregateResponse = await FinancingService.getAdminAggregate(params);
        if (aggregateResponse && aggregateResponse.data) {
          this.aggregateData = {
            ...aggregateResponse.data,
            currency: aggregateResponse.data.currency || 'AED'
          };
          this.monthlyAnalytics = aggregateResponse.data.monthly_analytics || [];
        }
        
        // Load preview requests (first 3)
        await this.loadPreviewRequests();
        
        // Load all repayments
        await this.loadRepayments();
        
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        this.error = formatErrorMessage(error.message || 'Failed to load dashboard data');
      } finally {
        this.loading = false;
      }
    },
    
    async loadPreviewRequests() {
      try {
        this.loadingRequests = true;
        const params = {
          page: 1,
          page_size: 5
        };
        
        if (this.selectedBusinesses.length > 0) {
          params.borrower_ids = this.selectedBusinesses.map(b => b.id).join(',');
        }
        
        const response = await FinancingService.getAdminRequests(params);
        if (response && response.data) {
          this.previewRequests = response.data || [];
          this.totalRequests = response.count || 0;
        }
      } catch (error) {
        console.error('Error loading preview requests:', error);
      } finally {
        this.loadingRequests = false;
      }
    },
    
    async loadRepayments() {
      try {
        this.loadingRepayments = true;
        const params = {
          page: 1,
          page_size: 5 
        };
        
        if (this.selectedBusinesses.length > 0) {
          params.borrower_ids = this.selectedBusinesses.map(b => b.id).join(',');
        }
        
        const response = await FinancingService.getAdminRepayments(params);
        if (response && response.data) {
          this.repayments = response.data || [];
          this.totalRepayments = response.count || 0;
        }
      } catch (error) {
        console.error('Error loading repayments:', error);
      } finally {
        this.loadingRepayments = false;
      }
    },
    
    formatCurrency(amount, currency) {
      if (!amount || isNaN(amount)) return this.$formatCurrency(0, currency);
      return this.$formatCurrency(amount, currency);
    },
    
    formatCurrencyCompact(amount, currency) {
      if (!amount || isNaN(amount)) return this.$formatCurrency(0, currency);
      
      // Format large numbers in compact form (K, M, B)
      if (amount >= 1000000000) {
        return this.$formatCurrency(amount / 1000000000, currency, { maximumFractionDigits: 1 }) + 'B';
      } else if (amount >= 1000000) {
        return this.$formatCurrency(amount / 1000000, currency, { maximumFractionDigits: 1 }) + 'M';
      } else if (amount >= 1000) {
        return this.$formatCurrency(amount / 1000, currency, { maximumFractionDigits: 1 }) + 'K';
      }
      
      return this.$formatCurrency(amount, currency);
    },
    
    formatMonth(monthString) {
      if (!monthString) return '';
      
      // Handle different month formats
      if (monthString.includes('-')) {
        // ISO date format
        const date = new Date(monthString);
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short' 
        });
      } else if (monthString.includes(' ')) {
        // "MMM YYYY" format
        return monthString;
      }
      
      return monthString;
    },
    
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    
    formatStatus(status) {
      const statusMap = {
        'PENDING_REVIEW': 'Pending Review',
        'AWAITING_APPROVAL': 'Awaiting approval',
        'APPROVED': 'Approved',
        'REJECTED': 'Rejected',
        'DISBURSED_AMOUNT': 'Disbursed Amount',
        'PARTIALLY_PAID': 'Partially Paid',
        'CLOSED': 'Closed'
      };
      return statusMap[status] || status;
    },
    
    getStatusClass(status) {
      const classMap = {
        'PENDING_REVIEW': 'status-pending-review',
        'AWAITING_APPROVAL': 'status-awaiting-approval',
        'APPROVED': 'status-approved',
        'REJECTED': 'status-rejected',
        'DISBURSED_AMOUNT': 'status-disbursed',
        'PARTIALLY_PAID': 'status-partially-paid',
        'CLOSED': 'status-closed'
      };
      return classMap[status] || 'status-default';
    },

    formatRepaymentStatus(status) {
      const statusMap = {
        'DUE': 'Due',
        'OVERDUE': 'Overdue',
        'DUE_SOON': 'Due Soon',
        'PAID': 'Paid',
        'PARTIALLY_PAID': 'Partially Paid',
        'PENDING': 'Pending'
      };
      return statusMap[status] || status;
    },

    getRepaymentStatusClass(status) {
      const classMap = {
        'DUE': 'status-due',
        'OVERDUE': 'status-overdue',
        'DUE_SOON': 'status-due-soon',
        'PAID': 'status-paid',
        'PARTIALLY_PAID': 'status-partially-paid',
        'PENDING': 'status-pending'
      };
      return classMap[status] || 'status-default';
    },

    isOverdue(dueDate) {
      if (!dueDate) return false;
      const today = new Date();
      const due = new Date(dueDate);
      return due < today;
    },
    
    // Business selection methods
    async loadInitialBusinesses() {
      try {
        const response = await CompaniesService.query({
          page: 1,
          page_size: 20
        });

        const businesses = response.data || [];
        
        this.availableBusinesses = businesses.map(business => ({
          id: business.id,
          name: business.name || `Business ${business.id}`
        }));

        this.totalBusinesses = response.count || 0;
        this.hasMoreBusinesses = !!response.next;
        this.businessPage = 1;

      } catch (err) {
        console.error('Failed to load initial businesses:', err);
        this.availableBusinesses = [];
        this.hasMoreBusinesses = false;
      }
    },

    async loadBusinesses(reset = false) {
      if (reset) {
        this.businessPage = 1;
        this.availableBusinesses = [];
        this.hasMoreBusinesses = false;
      }

      this.loadingBusinesses = true;
      try {
        const params = {
          page: this.businessPage,
          page_size: 20,
        };

        if (this.businessSearchTerm) {
          params.search = this.businessSearchTerm;
        }

        const response = await CompaniesService.query(params);
        
        const businesses = response.data || [];
        
        const formattedBusinesses = businesses.map(business => ({
          id: business.id,
          name: business.name || `Business ${business.id}`
        }));
        
        if (reset) {
          this.availableBusinesses = formattedBusinesses;
        } else {
          this.availableBusinesses = [...this.availableBusinesses, ...formattedBusinesses];
        }

        this.totalBusinesses = response.count || 0;
        this.hasMoreBusinesses = !!response.next;

      } catch (err) {
        console.error('Failed to load businesses:', err);
        if (reset) {
          this.availableBusinesses = [];
        }
        this.hasMoreBusinesses = false;
      } finally {
        this.loadingBusinesses = false;
      }
    },

    async loadMoreBusinesses() {
      if (this.hasMoreBusinesses && !this.loadingBusinesses && !this.isLoadingMore) {
        this.isLoadingMore = true;
        this.businessPage += 1;
        await this.loadBusinesses(false);
        this.isLoadingMore = false;
      }
    },

    async searchBusinesses() {
      if (this.businessSearchDebounce) clearTimeout(this.businessSearchDebounce);
      
      // Always show dropdown when typing
      this.showBusinessDropdown = true;
      
      if (this.businessSearchTerm.length >= 1) {
        // Debounce search for better UX
        this.businessSearchDebounce = setTimeout(() => this.loadBusinesses(true), 400);
      } else if (this.businessSearchTerm.length === 0) {
        // Load all businesses immediately when search is cleared
        await this.loadBusinesses(true);
      }
    },

    async onSearchFocus() {
      this.showBusinessDropdown = true;
      // Load businesses if not already loaded
      if (this.availableBusinesses.length === 0) {
        await this.loadBusinesses(true);
      }
    },

    onSearchBlur() {
      setTimeout(() => {
        const searchInput = this.$refs.businessSearchInput;
        
        if (searchInput && document.activeElement !== searchInput && 
            !this.isInteractingWithDropdown && 
            !this.isLoadingMore && 
            !this.loadingBusinesses) {
          this.showBusinessDropdown = false;
        }
      }, 150);
    },

    onDropdownMouseEnter() {
      this.isInteractingWithDropdown = true;
    },

    onDropdownMouseLeave() {
      this.isInteractingWithDropdown = false;
    },

    onDropdownScroll() {
      if (this.scrollDebounce) clearTimeout(this.scrollDebounce);
      
      this.scrollDebounce = setTimeout(() => {
        const businessList = this.$refs.businessList;
        if (!businessList) return;
        
        const scrollTop = businessList.scrollTop;
        const scrollHeight = businessList.scrollHeight;
        const clientHeight = businessList.clientHeight;
        const scrollBottom = scrollTop + clientHeight;
        const threshold = 50;
        
        if (scrollBottom >= scrollHeight - threshold && 
            this.hasMoreBusinesses && 
            !this.isLoadingMore && 
            !this.loadingBusinesses) {
          this.loadMoreBusinesses();
        }
      }, 100);
    },

    toggleBusinessSelection(business) {
      const index = this.selectedBusinesses.findIndex(b => b.id === business.id);
      if (index === -1) {
        this.selectedBusinesses.push(business);
      } else {
        this.selectedBusinesses.splice(index, 1);
      }
      this.updateDataWithBusinessFilter(); // Use optimized update method
    },

    removeBusinessSelection(business) {
      this.selectedBusinesses = this.selectedBusinesses.filter(b => b.id !== business.id);
      this.updateDataWithBusinessFilter(); // Use optimized update method
    },

    async updateDataWithBusinessFilter() {
      // Show loading state only for the data that needs to be updated
      this.loadingRequests = true;
      this.loadingRepayments = true; // Also show loading for repayments
      
      try {
        // Update aggregate data
        const params = {};
        if (this.selectedBusinesses.length > 0) {
          params.borrower_ids = this.selectedBusinesses.map(b => b.id).join(',');
        }
        
        // Load aggregate data in background without blocking UI
        const aggregatePromise = FinancingService.getAdminAggregate(params).then(response => {
          if (response && response.data) {
            this.aggregateData = {
              ...response.data,
              currency: response.data.currency || 'AED'
            };
            this.monthlyAnalytics = response.data.monthly_analytics || [];
          }
        }).catch(error => {
          console.error('Error updating aggregate data:', error);
        });
        
        // Update preview requests
        const requestsPromise = this.loadPreviewRequests();
        
        // Update repayments
        const repaymentsPromise = this.loadRepayments();
        
        // Wait for all to complete
        await Promise.all([aggregatePromise, requestsPromise, repaymentsPromise]);
        
      } catch (error) {
        console.error('Error updating data with business filter:', error);
      } finally {
        this.loadingRequests = false;
        this.loadingRepayments = false;
      }
    },

    clearAllBusinessSelections() {
      this.selectedBusinesses = [];
      this.updateDataWithBusinessFilter();
    },

    handleClickOutside(event) {
      const businessDropdown = this.$refs.businessDropdown;
      const searchInput = this.$refs.businessSearchInput;
      
      // Handle business dropdown
      if (businessDropdown && !businessDropdown.contains(event.target) && 
          searchInput && !searchInput.contains(event.target) &&
          !this.isLoadingMore && !this.loadingBusinesses) {
        this.showBusinessDropdown = false;
      }
    },

    handleKeydown(event) {
      if (!this.showBusinessDropdown) return;

      if (event.key === 'Escape') {
        this.showBusinessDropdown = false;
        
        const searchInput = this.$refs.businessSearchInput;
        if (searchInput && typeof searchInput.focus === 'function') {
          searchInput.focus();
        }
      }
    },


  },
  
  computed: {
    filteredBusinesses() {
      return this.availableBusinesses;
    }
  }
};
</script>

<style scoped lang="scss">
@import "./AnalyticsDashboard.scss";
</style> 